import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, Loader2, Send, ThumbsDown, ThumbsUp, Trash2, X } from 'lucide-react';
import { t } from '../../i18n/t';
import { streamAssistantChat } from '../../api/assistantStream';
import { setAssistantFeedback } from '../../api/assistantLog';
import { useAuth } from '../../contexts/AuthContext';
import {
  AssistantChatCitation,
  AssistantUiMessage,
} from '../../types/Assistant';
import './LopAssistantChat.css';

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function LopAssistantChat() {
  const { user } = useAuth();
  const currentTenantId = user?.currentTenant?.tenantId ?? null;
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantUiMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((p) => !p), []);

  // Global keyboard shortcut: Ctrl+\ to toggle. Ignores typing targets,
  // matching the AppShell's KeyG/KeyE pattern (physical key code so it
  // fires regardless of OS keyboard layout).
  useEffect(() => {
    function isTypingTarget(el: Element | null): boolean {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((el as HTMLElement).isContentEditable) return true;
      if (el.closest('.ag-cell-edit-wrapper')) return true;
      return false;
    }

    function onKeyDown(e: KeyboardEvent) {
      if (!e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'Backslash') return;
      // Allow toggle even when typing in non-AgGrid fields — Ctrl+\ is the chat key.
      if (document.querySelector('.ag-cell-inline-editing')) return;
      if (isTypingTarget(document.activeElement) && !isOpen) {
        // typing outside the chat — let user keep typing
        return;
      }
      e.preventDefault();
      toggle();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, toggle]);

  // Close on Escape when panel is open.
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Auto-focus the input when the panel opens.
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [isOpen]);

  // Auto-scroll to latest message.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  // Ref guard: two clicks/Enters inside one frame must not send twice (state lags a render).
  const sendingRef = useRef(false);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || sending || sendingRef.current) return;
    sendingRef.current = true;

    const userMsg: AssistantUiMessage = {
      id: newId(),
      role: 'user',
      text: trimmed,
      createdAt: Date.now(),
    };
    // Insert user message + an empty assistant placeholder we'll stream into.
    const assistantId = newId();
    const placeholder: AssistantUiMessage = {
      id: assistantId,
      role: 'assistant',
      text: '',
      createdAt: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg, placeholder]);
    setInput('');
    setSending(true);

    function patchAssistant(patch: Partial<AssistantUiMessage>) {
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m)),
      );
    }
    function appendText(fragment: string) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, text: m.text + fragment } : m,
        ),
      );
    }

    try {
      const stream = streamAssistantChat({
        message: trimmed,
        context: {
          currentRoute: location.pathname,
          currentTenantId: currentTenantId ?? null,
          selectedRowKey: null,
        },
      });
      for await (const evt of stream) {
        if (evt.kind === 'citations') {
          patchAssistant({ citations: evt.citations });
        } else if (evt.kind === 'delta') {
          appendText(evt.text);
        } else if (evt.kind === 'toolStart') {
          // Show the "..." chip so the user sees what the assistant is doing
          // during the DB round-trip (~200-500ms). No token is being emitted
          // during that window, so without this the UI feels stuck.
          patchAssistant({ activeToolLabel: evt.displayLabelHe });
        } else if (evt.kind === 'toolEnd') {
          patchAssistant({ activeToolLabel: null });
        } else if (evt.kind === 'done') {
          patchAssistant({
            usage: evt.usage,
            assistantLogId: evt.assistantLogId > 0 ? evt.assistantLogId : undefined,
            activeToolLabel: null,   // safety net if the stream aborted mid-tool
          });
        } else if (evt.kind === 'error') {
          patchAssistant({ error: evt.message, activeToolLabel: null });
        }
      }
    } catch (err) {
      patchAssistant({ error: err instanceof Error ? err.message : String(err) });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [input, sending, location.pathname, currentTenantId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleClear = useCallback(() => {
    if (sending) return;
    setMessages([]);
  }, [sending]);

  const handleFeedback = useCallback(async (messageId: string, value: 1 | 2) => {
    // Optimistic update — show the user's click immediately, even if the
    // network is slow. If the call fails we revert with a console warning;
    // we don't surface a toast for a like/dislike click.
    let logIdToSend: number | undefined;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId || !m.assistantLogId) return m;
        logIdToSend = m.assistantLogId;
        return { ...m, feedback: value };
      }),
    );
    if (!logIdToSend) return;
    try {
      await setAssistantFeedback(logIdToSend, value);
    } catch (err) {
      console.warn('Feedback save failed', err);
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback: undefined } : m)),
      );
    }
  }, []);

  // Hide the entire widget on the login screen / unauthenticated routes.
  // We let `useAuth` tell us whether there's a user; if not, render nothing.
  if (!user) return null;

  return (
    <>
      <button
        type="button"
        className="lop-assistant-fab"
        onClick={open}
        aria-label={t('assistant.openTitle')}
        title={`${t('assistant.openTitle')} (${t('assistant.keyboardHint')})`}
        hidden={isOpen}
      >
        <Bot size={22} />
      </button>

      <aside
        className={`lop-assistant-panel ${isOpen ? 'lop-assistant-panel--open' : ''}`}
        aria-hidden={!isOpen}
      >
        <header className="lop-assistant-panel__header">
          <div className="lop-assistant-panel__header-text">
            <h2>{t('assistant.panelTitle')}</h2>
            <p>{t('assistant.panelSubtitle')}</p>
          </div>
          <button
            type="button"
            className="lop-assistant-panel__icon-btn"
            onClick={handleClear}
            disabled={sending || messages.length === 0}
            aria-label={t('assistant.clear')}
            title={t('assistant.clear')}
          >
            <Trash2 size={16} />
          </button>
          <button
            type="button"
            className="lop-assistant-panel__icon-btn"
            onClick={close}
            aria-label={t('assistant.closeTitle')}
            title={t('assistant.closeTitle')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="lop-assistant-panel__messages">
          {messages.length === 0 && !sending && (
            <div className="lop-assistant-empty">
              <p className="lop-assistant-empty__title">{t('assistant.emptyTitle')}</p>
              <p className="lop-assistant-empty__hint">{t('assistant.emptyHint')}</p>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              isStreaming={sending && i === messages.length - 1 && m.role === 'assistant'}
              onFeedback={handleFeedback}
            />
          ))}

          <div ref={listEndRef} />
        </div>

        <footer className="lop-assistant-panel__input">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('assistant.inputPlaceholder')}
            rows={2}
            disabled={sending}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            aria-label={t('assistant.send')}
            title={t('assistant.send')}
            className="lop-assistant-panel__send"
          >
            {sending ? <Loader2 size={16} className="lop-assistant-spin" /> : <Send size={16} />}
          </button>
        </footer>
      </aside>
    </>
  );
}

function MessageBubble({
  message,
  isStreaming,
  onFeedback,
}: {
  message: AssistantUiMessage;
  isStreaming: boolean;
  onFeedback: (id: string, value: 1 | 2) => void;
}) {
  const isUser = message.role === 'user';
  const usage = message.usage;
  const usageTitle = useMemo(() => {
    if (!usage) return '';
    return t('assistant.usageTooltip', {
      input: usage.inputTokens,
      output: usage.outputTokens,
      chunks: usage.retrievedChunks,
    });
  }, [usage]);

  const showThinkingDots = isStreaming && !message.text && !message.error && !message.activeToolLabel;
  const showToolChip = isStreaming && !!message.activeToolLabel;

  return (
    <div
      className={`lop-assistant-msg lop-assistant-msg--${isUser ? 'user' : 'assistant'}`}
      title={usageTitle || undefined}
    >
      <div className="lop-assistant-msg__role">
        {isUser ? t('assistant.you') : t('assistant.assistantLabel')}
      </div>
      <div className="lop-assistant-msg__body">
        {message.error ? (
          <span className="lop-assistant-msg__error">
            {t('assistant.errorPrefix')}
            {message.error}
          </span>
        ) : showThinkingDots ? (
          <span className="lop-assistant-thinking-dots" aria-label={t('assistant.thinking')}>
            <span />
            <span />
            <span />
          </span>
        ) : (
          <>
            <SimpleMarkdown text={message.text} />
            {isStreaming && message.text && <span className="lop-assistant-caret" />}
          </>
        )}
        {showToolChip && (
          <div className="lop-assistant-tool-chip" aria-live="polite">
            <Loader2 size={12} className="lop-assistant-spin" />
            <span>{message.activeToolLabel}</span>
          </div>
        )}
      </div>
      {message.citations && message.citations.length > 0 && (
        <Citations citations={message.citations} />
      )}
      {!isUser && !isStreaming && !message.error && message.assistantLogId && (
        <div className="lop-assistant-feedback">
          <button
            type="button"
            className={`lop-assistant-feedback__btn ${message.feedback === 1 ? 'lop-assistant-feedback__btn--active' : ''}`}
            onClick={() => onFeedback(message.id, 1)}
            disabled={message.feedback !== undefined}
            aria-label={t('assistant.likeTooltip')}
            title={t('assistant.likeTooltip')}
          >
            <ThumbsUp size={14} />
          </button>
          <button
            type="button"
            className={`lop-assistant-feedback__btn ${message.feedback === 2 ? 'lop-assistant-feedback__btn--active lop-assistant-feedback__btn--negative' : ''}`}
            onClick={() => onFeedback(message.id, 2)}
            disabled={message.feedback !== undefined}
            aria-label={t('assistant.dislikeTooltip')}
            title={t('assistant.dislikeTooltip')}
          >
            <ThumbsDown size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function Citations({ citations }: { citations: AssistantChatCitation[] }) {
  // Dedupe by shortLabel — KB often returns multiple chunks from the same file.
  const unique = useMemo(() => {
    const seen = new Set<string>();
    const list: AssistantChatCitation[] = [];
    for (const c of citations) {
      if (!c.shortLabel || seen.has(c.shortLabel)) continue;
      seen.add(c.shortLabel);
      list.push(c);
    }
    return list;
  }, [citations]);

  if (unique.length === 0) return null;

  return (
    <div className="lop-assistant-citations">
      <span className="lop-assistant-citations__label">{t('assistant.sourcesLabel')}:</span>
      {unique.map((c) => (
        <span
          key={c.shortLabel}
          className="lop-assistant-citations__chip"
          title={c.sourceUri}
        >
          {c.shortLabel}
        </span>
      ))}
    </div>
  );
}

// Tiny markdown renderer — handles ## headings, paragraphs, line breaks,
// **bold**, `inline code`, and -/* bullet lists. We avoid a heavy markdown
// dependency for an MVP-scope component; if the bot's output gets richer,
// swap this for react-markdown.
function SimpleMarkdown({ text }: { text: string }) {
  const blocks = useMemo(() => splitBlocks(text), [text]);
  return (
    <>
      {blocks.map((block, i) => {
        if (block.kind === 'heading') {
          return (
            <h3 key={i} className="lop-assistant-md-h">
              {renderInline(block.text)}
            </h3>
          );
        }
        if (block.kind === 'list') {
          return (
            <ul key={i} className="lop-assistant-md-list">
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="lop-assistant-md-p">
            {renderInline(block.text)}
          </p>
        );
      })}
    </>
  );
}

type MdBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

function splitBlocks(text: string): MdBlock[] {
  const lines = text.split('\n');
  const blocks: MdBlock[] = [];
  let buffer: string[] = [];
  let bulletBuffer: string[] = [];

  function flushParagraph() {
    if (buffer.length === 0) return;
    blocks.push({ kind: 'paragraph', text: buffer.join('\n') });
    buffer = [];
  }
  function flushList() {
    if (bulletBuffer.length === 0) return;
    blocks.push({ kind: 'list', items: bulletBuffer });
    bulletBuffer = [];
  }

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'heading', text: trimmed.slice(3) });
      continue;
    }
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      flushParagraph();
      bulletBuffer.push(trimmed.slice(2));
      continue;
    }
    if (trimmed === '') {
      flushParagraph();
      flushList();
      continue;
    }
    flushList();
    buffer.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

function renderInline(text: string): React.ReactNode {
  // **bold** and `code` — minimal, safe.
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(<code key={key++}>{token.slice(1, -1)}</code>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}
